import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Tag, Palette, Package, CheckCircle, Send, Trash2, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const SortNoAssignment = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  
  // Sort No Selection - with server-side search
  const [sortNumbers, setSortNumbers] = useState([]);
  const [selectedSortNo, setSelectedSortNo] = useState('');
  const [sortNoPopoverOpen, setSortNoPopoverOpen] = useState(false);
  const [sortNoSearch, setSortNoSearch] = useState('');
  const [isSortNosLoading, setIsSortNosLoading] = useState(false);
  const [hasMoreSortNos, setHasMoreSortNos] = useState(false);
  
  // Debounced search for sort numbers
  const debouncedSortNoSearch = useDebounce(sortNoSearch, 300);
  
  // Color Selection
  const [colors, setColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [colorSearch, setColorSearch] = useState('');
  const [isColorsLoading, setIsColorsLoading] = useState(false);
  
  // Bundle scanning
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  // Fetch initial sort numbers (limited) and colors on mount
  useEffect(() => {
    fetchSortNumbers('');
    fetchColors();
  }, []);

  // Server-side search for sort numbers when debounced search changes
  useEffect(() => {
    fetchSortNumbers(debouncedSortNoSearch);
  }, [debouncedSortNoSearch]);

  // Focus bundle input when sort no and color are selected
  useEffect(() => {
    if (staffIdLocked && selectedSortNo && selectedColor) {
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  }, [staffIdLocked, selectedSortNo, selectedColor]);

  const fetchSortNumbers = useCallback(async (search) => {
    setIsSortNosLoading(true);
    try {
      const params = { limit: 50 };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      const response = await apiClient.get('/goods-transfer/sort-no-assignment/sort-numbers', { params });
      setSortNumbers(response.data.sort_numbers || []);
      setHasMoreSortNos(response.data.has_more || false);
    } catch (error) {
      console.error('Error fetching sort numbers:', error);
      toast.error('Failed to load sort numbers');
    } finally {
      setIsSortNosLoading(false);
    }
  }, []);

  const fetchColors = async () => {
    setIsColorsLoading(true);
    try {
      const response = await apiClient.get('/goods-transfer/sort-no-assignment/colors');
      setColors(response.data.colors || []);
    } catch (error) {
      console.error('Error fetching colors:', error);
      toast.error('Failed to load colors');
    } finally {
      setIsColorsLoading(false);
    }
  };

  // Filter colors based on search (client-side is fine for 51 colors)
  const filteredColors = useMemo(() => {
    if (!colorSearch) return colors;
    const search = colorSearch.toLowerCase();
    return colors.filter(color => 
      color.color_name?.toLowerCase().includes(search) ||
      `${color.red_code}-${color.green_code}-${color.blue_code}`.includes(search)
    );
  }, [colors, colorSearch]);

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
    }
  };

  const handleUnlockStaff = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setStaffName('');
    setSelectedSortNo('');
    setSelectedColor(null);
    setScannedBundles([]);
  };

  const handleSortNoSelect = (sortNo) => {
    setSelectedSortNo(sortNo);
    setSortNoPopoverOpen(false);
    setSortNoSearch('');
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setColorPopoverOpen(false);
    setColorSearch('');
  };

  const formatColorRgb = (color) => {
    if (!color) return '';
    return `rgb(${color.red_code || 0}, ${color.green_code || 0}, ${color.blue_code || 0})`;
  };

  const formatColorDisplay = (color) => {
    if (!color) return '';
    return `${color.color_name} (${color.red_code}-${color.green_code}-${color.blue_code})`;
  };

  const isSelectionComplete = staffIdLocked && selectedSortNo && selectedColor;

  const handleBundleScan = async (e) => {
    e.preventDefault();
    
    const barcode = bundleBarcode.trim().toUpperCase();
    if (!barcode) {
      toast.error('Please scan a bundle barcode');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    // Check if bundle already in session
    if (scannedBundles.some(b => b.barcode === barcode)) {
      toast.error('Bundle already scanned in this session');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post('/goods-transfer/sort-no-assignment/validate-bundle', null, {
        params: { bundle_barcode: barcode }
      });

      // Add bundle to scanned list
      setScannedBundles(prev => [{
        barcode: barcode,
        usn_count: response.data.usn_count || 0,
        packing_id: response.data.packing_id,
        packed_at: response.data.packed_at
      }, ...prev]);

      toast.success(`Bundle ${barcode} validated`);
      setBundleBarcode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate bundle');
      setBundleBarcode('');
    } finally {
      setIsProcessing(false);
      bundleInputRef.current?.focus();
    }
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
    toast.success('Bundle removed');
  };

  const handleSubmit = async () => {
    if (scannedBundles.length === 0) {
      toast.error('No bundles scanned');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/goods-transfer/sort-no-assignment/submit', {
        staff_id: staffId,
        sort_no: selectedSortNo,
        color_name: selectedColor.color_name,
        color_rgb: `${selectedColor.red_code}-${selectedColor.green_code}-${selectedColor.blue_code}`,
        bundle_barcodes: scannedBundles.map(b => b.barcode)
      });

      toast.success(response.data.message || 'Sort No assigned successfully');
      
      // Clear bundles but keep sort no and color for continuous scanning
      setScannedBundles([]);
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign Sort No');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalUsns = scannedBundles.reduce((sum, b) => sum + (b.usn_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/sections/goods-transfer')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Sort No Assignment</h1>
              <p className="text-sm text-[#9CA3AF]">Assign Sort No & Color to packed bundles</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Staff ID */}
        <Card className="bg-white border-2 border-orange-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <User className="w-5 h-5 text-orange-500" />
              Staff Identification
            </CardTitle>
            <CardDescription>Scan or enter staff ID to begin</CardDescription>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                ref={staffInputRef}
                onValidated={handleStaffValidated}
                autoFocus={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-lg">
                  <span className="font-mono font-semibold">{staffId}</span>
                  {staffName && <span className="ml-2 text-orange-600">- {staffName}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={handleUnlockStaff}>
                  Change
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Select Sort No and Color */}
        {staffIdLocked && (
          <Card className="bg-white border-2 border-cyan-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <Tag className="w-5 h-5 text-cyan-500" />
                Select Sort No & Color
              </CardTitle>
              <CardDescription>Choose sort number and color for assignment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sort No Dropdown with Search */}
                <div className="space-y-2">
                  <Label>Sort Number *</Label>
                  <Popover open={sortNoPopoverOpen} onOpenChange={setSortNoPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full h-12 justify-between text-left font-normal"
                        disabled={isSortNosLoading}
                        data-testid="sort-no-dropdown"
                      >
                        {isSortNosLoading ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading...
                          </div>
                        ) : selectedSortNo ? (
                          <span className="text-foreground font-medium">{selectedSortNo}</span>
                        ) : (
                          <span className="text-muted-foreground">Search & select sort number...</span>
                        )}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput 
                          placeholder="Type to search sort numbers..." 
                          value={sortNoSearch}
                          onValueChange={setSortNoSearch}
                        />
                        <CommandList className="max-h-[300px]">
                          {isSortNosLoading ? (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
                              <span className="ml-2 text-sm text-gray-500">Searching...</span>
                            </div>
                          ) : sortNumbers.length === 0 ? (
                            <CommandEmpty>
                              {sortNoSearch ? 'No sort number found.' : 'Type to search...'}
                            </CommandEmpty>
                          ) : (
                            <CommandGroup>
                              {sortNumbers.map((sortNo) => (
                                <CommandItem
                                  key={sortNo}
                                  value={sortNo}
                                  onSelect={() => handleSortNoSelect(sortNo)}
                                  className="cursor-pointer"
                                >
                                  <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-cyan-500" />
                                    <span className="font-medium">{sortNo}</span>
                                  </div>
                                </CommandItem>
                              ))}
                              {hasMoreSortNos && (
                                <div className="px-3 py-2 text-xs text-gray-500 border-t">
                                  Type more to narrow results...
                                </div>
                              )}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Color Dropdown with Search and Color Preview */}
                <div className="space-y-2">
                  <Label>Color *</Label>
                  <Popover open={colorPopoverOpen} onOpenChange={setColorPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full h-12 justify-between text-left font-normal"
                        disabled={isColorsLoading}
                        data-testid="color-dropdown"
                      >
                        {isColorsLoading ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading...
                          </div>
                        ) : selectedColor ? (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded border border-gray-300 flex-shrink-0" 
                              style={{ backgroundColor: formatColorRgb(selectedColor) }}
                            />
                            <span className="text-foreground">{formatColorDisplay(selectedColor)}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Search & select color...</span>
                        )}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Search color by name or RGB..." 
                          value={colorSearch}
                          onValueChange={setColorSearch}
                        />
                        <CommandList className="max-h-[300px]">
                          <CommandEmpty>No color found.</CommandEmpty>
                          <CommandGroup>
                            {filteredColors.map((color, idx) => (
                              <CommandItem
                                key={color.color_code || idx}
                                value={`${color.color_name} ${color.red_code}-${color.green_code}-${color.blue_code}`}
                                onSelect={() => handleColorSelect(color)}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center gap-3 w-full">
                                  <div 
                                    className="w-8 h-8 rounded border border-gray-300 flex-shrink-0" 
                                    style={{ backgroundColor: formatColorRgb(color) }}
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium">{color.color_name}</p>
                                    <p className="text-xs text-gray-500">
                                      ({color.red_code}-{color.green_code}-{color.blue_code})
                                    </p>
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Selection Summary */}
              {selectedSortNo && selectedColor && (
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <p className="text-sm font-semibold text-cyan-800 mb-2">Selected Assignment:</p>
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-xs text-cyan-600">Sort No:</span>
                      <p className="font-mono font-semibold text-lg">{selectedSortNo}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-cyan-600">Color:</span>
                      <div 
                        className="w-8 h-8 rounded border-2 border-cyan-300" 
                        style={{ backgroundColor: formatColorRgb(selectedColor) }}
                      />
                      <p className="font-semibold">{selectedColor.color_name}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Scan Bundles */}
        {isSelectionComplete && (
          <Card className="bg-white border-2 border-blue-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <Package className="w-5 h-5 text-blue-500" />
                Scan Bundles
              </CardTitle>
              <CardDescription>Scan bundle barcodes continuously to assign Sort No & Color</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBundleScan} className="flex gap-2">
                <Input
                  ref={bundleInputRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  placeholder="Scan Bundle Barcode..."
                  className="text-lg h-12 font-mono"
                  disabled={isProcessing}
                  autoComplete="off"
                  data-testid="bundle-barcode-input"
                />
                <Button 
                  type="submit" 
                  disabled={isProcessing} 
                  className="bg-blue-500 hover:bg-blue-600 h-12 px-6"
                  data-testid="add-bundle-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add'
                  )}
                </Button>
              </form>
              
              <p className="text-xs text-gray-500 mt-2">
                Bundles already in Sort No Assignment or FG Assignment will be rejected.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Scanned Bundles List */}
        {scannedBundles.length > 0 && (
          <Card className="bg-white border-none shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    Scanned Bundles ({scannedBundles.length})
                  </CardTitle>
                  <CardDescription>
                    Total USNs: {totalUsns} | Ready for assignment
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="bg-green-500 hover:bg-green-600"
                  data-testid="submit-assignment-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Assigning...' : 'Assign Sort No'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scannedBundles.map((bundle, index) => (
                  <div 
                    key={bundle.barcode} 
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                    data-testid={`scanned-bundle-${bundle.barcode}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-8">#{scannedBundles.length - index}</span>
                      <div>
                        <span className="font-mono font-semibold">{bundle.barcode}</span>
                        <span className="ml-2 text-xs text-gray-500">({bundle.usn_count} USNs)</span>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleRemoveBundle(bundle.barcode)}
                      className="text-red-500 hover:bg-red-100"
                      data-testid={`remove-bundle-${bundle.barcode}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Assignment Preview */}
        {scannedBundles.length > 0 && selectedSortNo && selectedColor && (
          <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-300">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-8 h-8 text-cyan-500" />
                  <div>
                    <p className="text-sm text-cyan-600">Ready to Assign</p>
                    <p className="font-semibold text-lg">
                      {scannedBundles.length} bundles ({totalUsns} USNs) → Sort No: {selectedSortNo}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-cyan-500" />
                  <div 
                    className="w-10 h-10 rounded-lg border-2 border-cyan-400" 
                    style={{ backgroundColor: formatColorRgb(selectedColor) }}
                  />
                  <span className="font-medium">{selectedColor.color_name}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SortNoAssignment;
